import { useEffect, useState } from 'react'
import { Button } from '../../Components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../Components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../Components/ui/dialog'
import { X } from 'lucide-react'
import { get } from '../../http/restservice'
import env from '../../env/env'

const removeCountryCode = (phoneNumber) => {
  return phoneNumber.replace(/^\+91/, '') // Removes +91 if it exists at the start
}

const FirstUserIdea = () => {
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [open, setOpen] = useState(false)
  const [users, setUsers] = useState([])
  const [userRes, setUserRes] = useState([])
  const [sortState, setSortState] = useState({
    status: true,
    createdOn: true,
    submittedOn: true,
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [singleUser, setSingleUser] = useState([])
  const [allUsers, setAllUsers] = useState([])

  useEffect(() => {
    try {
      if (allUsers && typeof allUsers === 'string') {
        const parsedUsers = JSON.parse(allUsers)

        const userResponse = parsedUsers?.userResponse

        const data = typeof userResponse === 'string' ? JSON.parse(userResponse) : userResponse

        console.log('Parsed UserResponse:', data) // Log the final parsed data
      } else {
        console.warn('allUsers is not a valid JSON string:', allUsers)
      }
    } catch (error) {
      console.error('Error parsing allUsers:', error)
    }
  }, [allUsers])

  const filteredUsers = users.filter((user) => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
    return fullName.includes(searchQuery.toLowerCase()) || user.phoneNumber.includes(searchQuery)
  })

  const transformedData = userRes.map((item) => {
    const key = Object.keys(item)[0] // Get the first key of the object
    const value = item[key] // Get the value of the key
    return { question: key, answer: value }
  })

  const handleSortByStatus = () => {
    const sortedUsers = [...users].sort((a, b) => {
      if (sortState.status) {
        return b.isCompleted - a.isCompleted // Sort true first
      } else {
        return a.isCompleted - b.isCompleted // Sort false first
      }
    })
    setUsers(sortedUsers)
    setSortState({ ...sortState, status: !sortState.status })
  }

  const handleSortByDate = (key) => {
    const sortedUsers = [...users].sort((a, b) => {
      const dateA = new Date(a[key === 'createdOn' ? 'createdTimestamp' : 'updatedTimestamp'])
      const dateB = new Date(b[key === 'createdOn' ? 'createdTimestamp' : 'updatedTimestamp'])

      if (sortState[key]) {
        return dateA - dateB // Sort ascending
      } else {
        return dateB - dateA // Sort descending
      }
    })
    setUsers(sortedUsers)
    setSortState({ ...sortState, [key]: !sortState[key] })
  }

  const handleViewMore = (invoice) => {
    fetchUserDetails(invoice)
    setSelectedInvoice(invoice)
    setOpen(true)
  }

  const fetchAllUsers = async () => {
    try {
      const response = await get(`${env.API_URL}adminservice/api/actionPlan/fetchAllFirstIdeas`)
      const users = JSON.parse(response?.data)
      setUsers(users)
      setAllUsers(response?.data)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchUserDetails = async (phoneNumber) => {
    try {
      const modifiedPhoneNumber = removeCountryCode(phoneNumber)
      const response = await get(
        `${env.API_URL}adminservice/api/actionPlan/fetchFirstIdeas?phoneNumber=${modifiedPhoneNumber}`,
      )
      setUserRes(response?.data?.userResponse)
      setSingleUser(response?.data)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    fetchAllUsers()
  }, [])

  const downloadCSV = () => {
    if (!selectedInvoice || !userRes.length) {
      console.error('No user selected or user response data is empty')
      return
    }

    const data = {
      id: selectedInvoice,
      phoneNumber: selectedInvoice,
      userResponse: userRes,
      isCompleted: users.find((user) => user.phoneNumber === selectedInvoice)?.isCompleted || false,
      createdTimestamp: singleUser.createdTimestamp,
      updatedTimestamp: singleUser.updatedTimestamp,
    }

    const headers = ['Field', 'Value']
    const rows = []

    // Add basic user details
    rows.push(['ID', data.id])
    rows.push(['Phone Number', data.phoneNumber])
    rows.push(['Is Completed', data.isCompleted ? 'Yes' : 'No'])
    rows.push(['Created Timestamp', data.createdTimestamp])
    rows.push(['Updated Timestamp', data.updatedTimestamp])

    // Add userResponse details
    data.userResponse.forEach((response) => {
      Object.entries(response).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          rows.push([key, value.join(', ')])
        } else if (typeof value === 'object') {
          rows.push([key, JSON.stringify(value)])
        } else {
          rows.push([key, value])
        }
      })
    })

    // Generate CSV content
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((row) => row.join(','))].join('\n')

    // Create and trigger download
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', 'user_details.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadAllUsersCSV = () => {
    if (!users || users.length === 0) {
      console.error('No users available to export.')
      return
    }

    const headers = ['ID', 'First Name', 'Last Name', 'Phone Number', 'Status', 'User Response']
    const rows = users.map((user) => [
      user.id,
      user.firstName,
      user.lastName,
      user.phoneNumber,
      user.isCompleted ? 'Completed' : 'Pending',
      user.userResponse ? JSON.stringify(user.userResponse) : 'N/A',
    ])

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((row) => row.join(','))].join('\n')

    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', 'all_users.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  function getDateAndTime(timestamp) {
    const dateObj = new Date(timestamp)

    // Extract date components
    const year = dateObj.getFullYear()
    const month = String(dateObj.getMonth() + 1).padStart(2, '0') // Months are 0-based
    const day = String(dateObj.getDate()).padStart(2, '0')

    // Extract time components
    const hours = String(dateObj.getHours()).padStart(2, '0')
    const minutes = String(dateObj.getMinutes()).padStart(2, '0')
    const seconds = String(dateObj.getSeconds()).padStart(2, '0')

    // Format the date and time
    const date = `${year}-${month}-${day}`
    const time = `${hours}:${minutes}:${seconds}`

    return { date, time }
  }

  return (
    <>
      <div className='mb-4 flex gap-[10px]'>
        <input
          type='text'
          placeholder='Search by name or phone number'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='border p-2 rounded w-full flex-[5]'
        />
        <button
          className='bg-primary-600 text-white text-sm p-2 flex-1'
          onClick={downloadAllUsersCSV}
        >
          Download All
        </button>
      </div>

      <Table>
        <TableHeader className='bg-[#F4F7FE]'>
          <TableRow className='border-none'>
            <TableHead className='text-primary-400 h-0 w-[300px] rounded-l-xl'>Name</TableHead>
            <TableHead className='text-primary-400 flex items-center'>Mobile Number</TableHead>
            <TableHead className='text-primary-400 h-0 text-left rounded-r-xl'>
              Status{' '}
              <button onClick={handleSortByStatus} className='ml-2'>
                {sortState.status ? '▲' : '▼'}
              </button>
            </TableHead>
            <TableHead className='text-primary-400 h-0 text-left rounded-r-xl'>
              Submitted On{' '}
              <button onClick={() => handleSortByDate('createdOn')} className='ml-2'>
                {sortState.createdOn ? '▲' : '▼'}
              </button>
            </TableHead>
            <TableHead className='text-primary-400 h-0 text-left rounded-r-xl'>
              Updated On{' '}
              <button onClick={() => handleSortByDate('submittedOn')} className='ml-2'>
                {sortState.submittedOn ? '▲' : '▼'}
              </button>
            </TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredUsers.map((user) => {
            const { date: createdDate, time: createdTime } = getDateAndTime(user.createdTimestamp)
            const { date: updatedDate, time: updatedTime } = getDateAndTime(user.updatedTimestamp)
            return (
              <TableRow key={user.id}>
                <TableCell>{user.firstName + ' ' + user.lastName}</TableCell>
                <TableCell className='font-medium'>{user.phoneNumber}</TableCell>
                <TableCell>
                  <p
                    className={`flex justify-center items-center rounded-lg ${
                      user.isCompleted
                        ? 'bg-green-300 font-semibold py-1'
                        : 'bg-red-400 font-semibold py-1'
                    }`}
                  >
                    {user.isCompleted === true ? 'Completed' : 'Pending'}
                  </p>
                </TableCell>
                <TableCell className='font-medium'>
                  {createdDate} / {createdTime}
                </TableCell>
                <TableCell className='font-medium'>
                  {updatedDate} / {updatedTime}
                </TableCell>
                <TableCell className='text-right'>
                  <Button onClick={() => handleViewMore(user.phoneNumber)}>View More</Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {selectedInvoice && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className='sm:max-w-[1125px]'>
            <DialogHeader>
              <DialogTitle className='text-2xl text-[#1D3B7A] font-semibold flex justify-between'>
                <p>User Response</p>
                <span
                  className='bg-white rounded-full p-2 cursor-pointer'
                  onClick={() => setOpen(false)}
                >
                  <X className='h-4 w-4 text-black-300' />
                </span>
              </DialogTitle>
              <DialogDescription>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Question</TableHead>
                      <TableHead>Answer</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transformedData.map((res) => (
                      <TableRow key={res.question}>
                        <TableCell className='font-medium'>{res.question}</TableCell>
                        <TableCell className='font-medium'>
                          {res.question === 'investment' && typeof res.answer === 'object' ? (
                            <>
                              <p>Type: {res.answer.type.join(', ')}</p>
                              <p>Other: {res.answer.other || 'N/A'}</p>
                              <p>Checked: {res.answer.isChecked ? 'Yes' : 'No'}</p>
                            </>
                          ) : res.question === 'lumpSum' && typeof res.answer === 'object' ? (
                            <>
                              <p>Answer: {res.answer.ans}</p>
                              <p>Amount: {res.answer.amount || 'N/A'}</p>
                            </>
                          ) : Array.isArray(res.answer) ? (
                            res.answer.join(', ')
                          ) : typeof res.answer === 'object' ? (
                            JSON.stringify(res.answer)
                          ) : (
                            res.answer
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <button
                  className='mt-4 bg-blue-500 text-white px-4 py-2 rounded'
                  onClick={downloadCSV}
                >
                  Download CSV
                </button>
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default FirstUserIdea
